import { memo, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Box, BoxProps, Typography } from '@material-ui/core';
import { TextColor } from '@setvi/shared/enums';
import { Container } from './styled';
import ProductTextInput from './text';
import WebUrlInput from './url';
import Equipment from './equipment';
import ImageInput from './image';
import CompetitorForm from './competitor';
import { Options, ProductMatchSubmitButton } from './components';
import { SearchFormOptions } from '../../enums';
import { ProductMatchValues, RankingsValues } from '../..';
import SButton from '../../../../sbutton';
import { distances } from '../../../../../styles';
import { useDialog } from '../../../../../providers';
import Rankings from '../../../rankings';

export type AISearchValues = {
  [ProductMatchValues.Url]: string;
  [ProductMatchValues.Text]: string;
  [ProductMatchValues.Equipment]: string;
  [ProductMatchValues.ProductName]: string;
  [ProductMatchValues.ManufacturerName]: string;
  [ProductMatchValues.ManufacturerSku]: string;
  [ProductMatchValues.Image]: string;
  [ProductMatchValues.Description]: string;
  [RankingsValues.PrivateLabel]: boolean;
  [RankingsValues.StockProduct]: boolean;
  [RankingsValues.Vendor]: boolean;
};

export interface SearchFormProps {
  options?: SearchFormOptions[];
  defaultOption?: SearchFormOptions;
  values?: AISearchValues;
  expanded?: boolean;
  disableButton?: boolean;
  height?: BoxProps['height'];
  textRows?: number;
  urlRows?: number;
  imageTextRows?: number;
  onChange?: (value: { [key: string]: string | boolean }) => void;
  onSubmit: (
    type: SearchFormOptions,
    search: {
      value: string;
      [key: string]: string | boolean;
    }
  ) => void;
}
const rankingsDialogId = 'rankings';

const SearchFormMemo = ({
  options,
  defaultOption,
  values,
  height,
  expanded = true,
  disableButton,
  textRows = 8,
  urlRows = 8,
  imageTextRows = 6,
  onSubmit,
  onChange
}: SearchFormProps) => {
  const [inputValid, setInputValid] = useState(true);
  const [tooltipTitle, setTooltipTitle] = useState<string | undefined>(
    undefined
  );
  const [currentOption, setCurrentOption] = useState<SearchFormOptions>(
    (defaultOption as SearchFormOptions) || SearchFormOptions.url
  );
  const { openDialog, closeDialog } = useDialog();
  const flags = useFlags();

  const handleSubmit = (
    type: SearchFormOptions,
    search: {
      value: string;
      [key: string]: string | boolean;
    }
  ) =>
    onSubmit(type, {
      ...search,
      [RankingsValues.PrivateLabel]: values?.[RankingsValues.PrivateLabel],
      [RankingsValues.StockProduct]: values?.[RankingsValues.StockProduct],
      [RankingsValues.Vendor]: values?.[RankingsValues.Vendor]
    });

  return (
    <Container height={height}>
      {currentOption === SearchFormOptions.url && (
        <WebUrlInput
          rows={urlRows}
          values={values}
          onSubmit={handleSubmit}
          onChange={onChange}
          handleInputValidation={setInputValid}
        />
      )}
      {flags.equipmentFinder &&
        currentOption === SearchFormOptions.equipment && (
          <Equipment
            values={values}
            expanded={expanded}
            disableButton={disableButton}
            onSubmit={handleSubmit}
            onChange={onChange}
          />
        )}
      {currentOption === SearchFormOptions.text && (
        <ProductTextInput
          rows={textRows}
          values={values}
          onSubmit={handleSubmit}
          onChange={onChange}
          handleInputValidation={setInputValid}
        />
      )}
      {currentOption === SearchFormOptions.image && (
        <ImageInput
          values={values}
          rows={imageTextRows}
          onSubmit={handleSubmit}
          onChange={onChange}
          handleInputValidation={(invalid: boolean, tooltip?: string) => {
            setInputValid(invalid);
            setTooltipTitle(tooltip);
          }}
        />
      )}
      {flags?.vendorMatch && currentOption === SearchFormOptions.competitor && (
        <CompetitorForm
          values={values}
          onSubmit={handleSubmit}
          onChange={onChange}
          handleInputValidation={setInputValid}
        />
      )}

      <Box display="flex" flexDirection="column">
        {expanded && (
          <Typography
            style={{
              fontSize: 16,
              lineHeight: '24px',
              marginTop: 8,
              color: TextColor.Main
            }}>
            Select your searching input method
          </Typography>
        )}
        <Box display="flex" gridGap="8px" mt={2} justifyContent="space-between">
          {options.length > 0 && (
            <Options
              options={options}
              expanded={expanded}
              currentOption={currentOption}
              onClick={newOption => {
                setCurrentOption(newOption);
                setInputValid(false);
              }}
            />
          )}
          <Box display="flex" gridGap={distances.px.base}>
            {flags?.aiRanking && (
              <SButton
                type="button"
                variant="outlined"
                onClick={e => {
                  e.stopPropagation();
                  openDialog(
                    <Rankings
                      rankings={{
                        [RankingsValues.PrivateLabel]:
                          values?.[RankingsValues.PrivateLabel],
                        [RankingsValues.StockProduct]:
                          values?.[RankingsValues.StockProduct],
                        [RankingsValues.Vendor]: values?.[RankingsValues.Vendor]
                      }}
                      onConfirm={newRankings => {
                        onChange({
                          [RankingsValues.PrivateLabel]:
                            newRankings[RankingsValues.PrivateLabel],
                          [RankingsValues.StockProduct]:
                            newRankings[RankingsValues.StockProduct],
                          [RankingsValues.Vendor]:
                            newRankings[RankingsValues.Vendor]
                        });
                        closeDialog(rankingsDialogId);
                      }}
                      onClose={() => closeDialog(rankingsDialogId)}
                    />,
                    {
                      id: rankingsDialogId
                    }
                  );
                }}
                style={{ borderRadius: 120 }}>
                Rankings
              </SButton>
            )}
            <ProductMatchSubmitButton
              expanded={expanded}
              tooltipTitle={tooltipTitle}
              disabled={disableButton || !inputValid}
            />
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

const SearchForm = memo(SearchFormMemo);
export default SearchForm;
